import {
  hasPricingOptions,
  hasSinglePricingOption,
  isAvailableDonationTicket,
  saleScheduled,
} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {AddSmall} from '@wix/wix-ui-icons-common'
import {ChevronLeftSmall, ChevronRightSmall, DeleteSmall} from '@wix/wix-ui-icons-common/on-stage'
import {Divider, HoverStyles, TextButton, TextButtonPriority} from '@wix/wix-ui-tpa/cssVars'
import {useEnvironment, useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React from 'react'
import {useColors} from '../../../../../hooks/colors'
import {useAppActions, useAppSelector} from '../../../../../hooks/runtime'
import {isInMobileBasket} from '../../../../../selectors/seating/mode'
import {
  getPlaceInfoText,
  isPlaceArea,
  isPlaceInBasket,
  shouldShowSeatInfo,
} from '../../../../../selectors/seating/place'
import {getAreaCounterValue} from '../../../../../selectors/seating/places'
import {a11yPlaceFeesId} from '../../../../event-details/tickets-picker-new/a11y'
import {TicketAreaCounter} from '../ticket-area-counter/ticket-area-counter'
import {TicketDonation} from '../ticket-donation/ticket-donation'
import {TicketFees} from '../ticket-fees/ticket-fees'
import {SeatInfo} from '../ticket-info/seat-info/seat-info'
import {WholeElementReservation} from '../whole-element-reservation/whole-element-reservation'
import {TicketActionProps} from './interfaces'
import {classes} from './ticket-action.st.css'

export const TicketAction = ({
  showAccessibilityMode,
  inDescription,
  inDrawer,
  onClick,
  onTicketMoreInfoClick,
  place,
  basketView = false,
  origin,
}: TicketActionProps) => {
  const {isRTL, isMobile} = useEnvironment()
  const {darkColor} = useColors()
  const {t} = useTranslation()
  const placeInfoText = getPlaceInfoText(place, t)
  const area = isPlaceArea(place)
  const inBasket = isPlaceInBasket(place)
  const showSeatInfo = shouldShowSeatInfo(place)
  const pricingOptions = hasPricingOptions(place.ticket)
  const singlePricingOption = hasSinglePricingOption(place.ticket)
  const {setAreaCounterValue} = useAppActions()
  const setValue = (count: number) => setAreaCounterValue({placeId: place.id, count})
  const scheduled = place.ticket ? saleScheduled(place.ticket) : undefined
  const feesId = a11yPlaceFeesId(place.id)

  const areaCounterValue = useAppSelector(state => getAreaCounterValue(state, place.id))
  const mobileBasketView = useAppSelector(isInMobileBasket)

  const mobileMapPricingOptionsDescription =
    isMobile && area && pricingOptions && !singlePricingOption && !mobileBasketView && !showAccessibilityMode

  let addButtonText = t('seatings_addToBasket')

  if (mobileMapPricingOptionsDescription) {
    addButtonText = t('seatings_continue')
  } else if (inBasket) {
    addButtonText = t('seatings_removeFromBasket')
  } else if (pricingOptions && !area && !singlePricingOption) {
    if (showAccessibilityMode) {
      addButtonText = t('seatings_selectPrice')
    } else if (isMobile) {
      addButtonText = t('seatings_continue')
    }
  }

  const showIcon = addButtonText === t('seatings_addToBasket') || addButtonText === t('seatings_removeFromBasket')

  const handleAddButtonClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation()
    onClick?.(e)
  }

  return (
    <>
      {isAvailableDonationTicket(place.ticket) && !inDrawer ? (
        <>
          <TicketDonation place={place} describedBy={feesId} />
          <TicketFees place={place} id={feesId} />
        </>
      ) : null}
      {!scheduled && area && !inDrawer && !mobileMapPricingOptionsDescription ? (
        <TicketAreaCounter value={areaCounterValue} setValue={setValue} origin={origin} place={place} />
      ) : null}
      {basketView || inDescription || showAccessibilityMode ? (
        <WholeElementReservation
          place={place}
          basketView={basketView}
          inDescription={inDescription}
          showAccessibilityMode={showAccessibilityMode}
        />
      ) : null}
      {showSeatInfo && inDrawer ? (
        <div className={classes.seatInfoContainer}>
          <Divider className={classes.divider} />
          <SeatInfo place={place} />
        </div>
      ) : null}
      {(showAccessibilityMode || basketView || inDrawer) &&
      (showSeatInfo || place.ticket?.description) &&
      !inDescription ? (
        <TextButton
          data-hook={DH.MORE_BUTTON}
          className={classNames(classes.moreInfo, {
            [classes.buttonNotWired]: !darkColor,
            [classes.moreInfoWithCounter]: area,
          })}
          suffixIcon={isRTL ? <ChevronLeftSmall /> : <ChevronRightSmall />}
          onClick={e => onTicketMoreInfoClick?.(e)}
          aria-label={`${t('seatings_ticket_moreInfo')} ${placeInfoText}`}
          priority={TextButtonPriority.link}
          hoverStyle={HoverStyles.underline}
        >
          {t('seatings_ticket_moreInfo')}
        </TextButton>
      ) : null}
      {!scheduled && !inDrawer ? (
        <div className={classes.addButtonContainer}>
          <Divider className={classes.divider} />
          <TextButton
            onClick={handleAddButtonClick}
            data-hook={DH.ADD}
            className={classNames(classes.addButton, {
              [classes.buttonNotWired]: !darkColor,
            })}
            prefixIcon={showIcon ? inBasket ? <DeleteSmall /> : <AddSmall /> : null}
            aria-label={`${addButtonText} ${placeInfoText}`}
            priority={TextButtonPriority.link}
            hoverStyle={HoverStyles.underline}
          >
            {addButtonText}
          </TextButton>
        </div>
      ) : null}
    </>
  )
}
